<template>
  <v-dialog
    v-model="dialog"
    content-class="crud-dialog"
    persistent
    max-width="600px"
  >
    <v-card class="crud-card">
      <v-card-title class="crud-card-title">
        <v-row no-gutters>
          <v-col cols="11" class="text-left">
            <h2 class="text-h5">Create lead</h2>
          </v-col>
          <v-col cols="1">
            <v-icon
              class="modal-close"
              color="black"
              @click.stop="closeDialog()"
              >{{ icons.mdiClose }}</v-icon
            >
          </v-col>
        </v-row>
      </v-card-title>
      <v-divider />
      <v-card-text class="crud-card-text">
        <v-row no-gutters class="mb-6">
          <v-col cols="12" sm="6" class="pr-2">
            <v-card class="statistics-card">
              <v-card-title class="statistics-title"
                >Total leads created</v-card-title
              >
              <v-card-text class="statistics-number">
                {{ leadProviderDashboard.numberOfCreatedTestators }}
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" class="pl-2">
            <v-card class="statistics-card">
              <v-card-title class="statistics-title"
                >Wills downloaded</v-card-title
              >
              <v-card-text class="statistics-number">
                {{ leadProviderDashboard.numberOfWillsDownloaded }}
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <ValidationObserver ref="observer">
          <v-form @submit.prevent="register">
            <v-row>
              <v-col cols="12">
                <ValidationProvider
                  id="first_names"
                  v-slot="{ errors }"
                  name="first_names"
                  rules="required"
                >
                  <v-text-field
                    v-model.trim="formData.firstNames"
                    label="First names*"
                    :error-messages="errors"
                    autocomplete="off"
                    :readonly="false"
                  />
                </ValidationProvider>
              </v-col>
              <v-col cols="12">
                <ValidationProvider
                  id="last_name"
                  v-slot="{ errors }"
                  name="last_name"
                  rules="required"
                >
                  <v-text-field
                    v-model.trim="formData.lastName"
                    label="Last name*"
                    :error-messages="errors"
                  />
                </ValidationProvider>
              </v-col>
              <v-col cols="12">
                <ValidationProvider
                  id="email"
                  v-slot="{ errors }"
                  name="email"
                  rules="required|email"
                >
                  <v-text-field
                    id="email"
                    v-model.trim="formData.email"
                    :error-messages="errors"
                    label="Email"
                    autocomplete="new-email"
                    :readonly="false"
                  />
                </ValidationProvider>
              </v-col>
              <v-col cols="12">
                <ValidationProvider
                  id="cell_number"
                  v-slot="{ errors }"
                  name="contact number"
                  :rules="{
                    required: true,
                    phoneIntl: [formData.cellCountryCode],
                  }"
                >
                  <v-text-field
                    v-model.trim="formData.phone"
                    label="Contact number*"
                    :error-messages="errors"
                  >
                    <template #prepend>
                      <vue-tel-input
                        :preferred-countries="['ZA', 'GB']"
                        :default-country="formData.cellCountryCode"
                        @country-changed="countrySelected"
                      >
                        <template #arrow-icon>
                          <strong>{{ countryDialCode }}</strong>
                        </template>
                      </vue-tel-input>
                    </template>
                  </v-text-field>
                </ValidationProvider>
              </v-col>
              <v-col cols="12">
                <ValidationProvider
                  id="cell_number"
                  v-slot="{ errors }"
                  name="alternative contact number"
                  :rules="{
                    required: false,
                    phoneIntl: [formData.alternativeCellCountryCode],
                  }"
                >
                  <v-text-field
                    v-model.trim="formData.phoneAlternative"
                    label="Alternative contact number"
                    :error-messages="errors"
                  >
                    <template #prepend>
                      <vue-tel-input
                        :preferred-countries="['ZA', 'GB']"
                        :default-country="formData.cellCountryCode"
                        @country-changed="alternativeCountrySelected"
                      >
                        <template #arrow-icon>
                          <strong>{{ alternativeCountryDialCode }}</strong>
                        </template>
                      </vue-tel-input>
                    </template>
                  </v-text-field>
                </ValidationProvider>
              </v-col>
            </v-row>
            <v-divider class="my-4" />
            <v-row>
              <v-col cols="12">
                <v-checkbox
                  v-model="scheduleSync"
                  label="Schedule time to create lead"
                  color="primary"
                ></v-checkbox>
              </v-col>
            </v-row>
            <v-row v-if="scheduleSync">
              <v-col cols="12" md="6">
                <ValidationProvider
                  v-slot="{ errors, validate }"
                  name="sync date"
                  :rules="scheduleSync ? 'required' : ''"
                  ref="syncDateProvider"
                >
                  <v-menu
                    v-model="dateMenu"
                    :close-on-content-click="false"
                    :nudge-right="40"
                    transition="scale-transition"
                    offset-y
                    min-width="290px"
                  >
                    <template #activator="{ on }">
                      <v-text-field
                        v-model="syncDate"
                        label="Sync Date"
                        :prepend-icon="icons.mdiCalendar"
                        readonly
                        v-on="on"
                        :error-messages="errors"
                        @blur="validate"
                        @input="validate"
                      />
                    </template>
                    <v-date-picker
                      ref="dateMenu"
                      v-model="syncDate"
                      :min="currentDate"
                      @input="
                        dateMenu = false;
                        $refs.syncDateProvider.validate();
                      "
                    />
                  </v-menu>
                </ValidationProvider>
              </v-col>
              <v-col cols="12" md="6">
                <ValidationProvider
                  v-slot="{ errors }"
                  name="sync time"
                  :rules="scheduleSync ? 'required' : ''"
                >
                  <v-select
                    v-model="syncTime"
                    :items="timeOptions"
                    label="Sync Time"
                    :prepend-icon="icons.mdiClock"
                    item-text="label"
                    item-value="value"
                    return-object="false"
                    clearable
                    :error-messages="errors"
                    menu-props="content-class: time-select-menu"
                    @click:menu="scrollToSelectedTime"
                  />
                </ValidationProvider>
              </v-col>
            </v-row>
            <v-card-actions class="crud-card-actions">
              <v-row no-gutters>
                <v-col cols="12" class="text-center">
                  <v-btn
                    class="right-edit-btn"
                    rounded
                    depressed
                    large
                    color="primary"
                    :loading="buttonLoading"
                    @click="register"
                  >
                    Submit Lead
                  </v-btn>
                </v-col>
              </v-row>
            </v-card-actions>
          </v-form>
        </ValidationObserver>
      </v-card-text>
    </v-card>
    <ErrorSnackbar v-model="showError" :error="errorMessage" />
    <SuccessSnackbar v-model="showSuccess" :message="successMessage" />
  </v-dialog>
</template>

<script>
  import { mdiClose, mdiCalendar, mdiClock } from '@mdi/js';
  import {
    REGISTER_LEAD_PROVIDER_MUTATION,
    LEAD_PROVIDER_DASHBOARD_QUERY,
  } from '../../constants/graphql/lead-provider';
  import ErrorSnackbar from '../common/ErrorSnackbar.vue';
  import SuccessSnackbar from '../common/SuccessSnackbar.vue';

  export default {
    name: 'DFASPPLeadDialog',
    components: {
      ErrorSnackbar,
      SuccessSnackbar,
    },
    props: {
      value: {
        type: Boolean,
        required: true,
      },
    },
    data() {
      return {
        icons: {
          mdiClose,
          mdiCalendar,
          mdiClock,
        },
        leadProviderDashboard: {},
        showSuccess: false,
        successMessage: '',
        buttonLoading: false,
        errorMessage: '',
        countryDialCode: '+27',
        alternativeCountryDialCode: '+27',
        formData: {
          firstNames: '',
          lastName: '',
          email: '',
          adviserCode: '',
          company: '',
          cellCountryCode: 'ZA',
          alternativeCellCountryCode: 'ZA',
          phone: '',
          phoneAlternative: '',
        },
        showError: false,
        scheduleSync: false,
        dateMenu: false,
        timeMenu: false,
        syncDate: null,
        syncTime: null,
        timeOptions: this.generateTimeOptions(),
      };
    },
    computed: {
      dialog: {
        get() {
          return this.value;
        },
        set(value) {
          this.$emit('input', value);
        },
      },
      currentDate() {
        return new Date().toJSON().slice(0, 10);
      },
    },
    watch: {
      scheduleSync(val) {
        if (val) {
          if (!this.syncDate) {
            this.syncDate = this.currentDate;
          }
          if (!this.syncTime) {
            const now = new Date();
            let minutes = now.getMinutes();
            minutes = Math.round(minutes / 10) * 10;
            if (minutes === 60) {
              now.setHours(now.getHours() + 1);
              minutes = 0;
            }
            const hour = now.getHours().toString().padStart(2, '0');
            const minute = minutes.toString().padStart(2, '0');
            this.syncTime = `${hour}:${minute}`;
          }
        } else {
          this.syncDate = null;
          this.syncTime = null;
        }
      },
    },
    methods: {
      countrySelected(val) {
        this.formData.cellCountryCode = val.iso2;
        this.countryDialCode = '+' + val.dialCode.toString();
      },
      alternativeCountrySelected(val) {
        this.formData.alternativeCellCountryCode = val.iso2;
        this.alternativeCountryDialCode = '+' + val.dialCode.toString();
      },
      clearFormData() {
        this.formData = {
          firstNames: '',
          lastName: '',
          email: '',
          adviserCode: '',
          company: '',
          cellCountryCode: 'ZA',
          alternativeCellCountryCode: 'ZA',
          phone: '',
          phoneAlternative: '',
        };
        this.$refs.observer.reset();
      },
      onError(error) {
        this.buttonLoading = false;
        this.showError = true;
        if (error.message.includes('GraphQL error:')) {
          this.errorMessage = error.message
            .replace('GraphQL error:', '')
            .trim();
        } else {
          this.errorMessage =
            "Something went wrong, we're looking into it. Please try again.";
          throw error;
        }
      },
      register() {
        this.$refs.observer.validate().then(valid => {
          if (valid) {
            this.buttonLoading = true;
            const firstNames = this.formData.firstNames;
            const lastName = this.formData.lastName;
            const email = this.formData.email;
            const phone = this.formData.phone;
            const phoneAlternative = this.formData.phoneAlternative;
            const createdByUserId = parseInt(this.$store.getters.userId);
            const contactCountryCode = this.formData.cellCountryCode;
            const alternativeContactCountryCode =
              this.formData.alternativeCellCountryCode;

            // Combine date and time for scheduling
            let scheduledDateTime = null;
            const timeString =
              typeof this.syncTime === 'string'
                ? this.syncTime
                : (this.syncTime && this.syncTime.value) || '';
            if (this.scheduleSync && this.syncDate && timeString) {
              // Create a date object in local timezone
              const [hours, minutes] = timeString.split(':');
              const localDate = new Date(this.syncDate);
              localDate.setHours(parseInt(hours, 10));
              localDate.setMinutes(parseInt(minutes, 10));
              localDate.setSeconds(0);
              localDate.setMilliseconds(0);

              // Convert to ISO string which includes timezone offset
              scheduledDateTime = localDate.toISOString();
            }

            this.$apollo
              .mutate({
                mutation: REGISTER_LEAD_PROVIDER_MUTATION,
                variables: {
                  email,
                  phone,
                  phoneAlternative,
                  firstNames,
                  lastName,
                  createdByUserId,
                  contactCountryCode,
                  alternativeContactCountryCode,
                  scheduledDateTime,
                },
              })
              .then(result => {
                if (result.data.registerLeadProvider.statusCode === 200) {
                  this.successMessage = 'Lead has been added successfully!';
                  this.leadProviderDashboard.numberOfCreatedTestators =
                    result.data.registerLeadProvider.numberOfCreatedTestators;
                  this.leadProviderDashboard.numberOfWillsDownloaded =
                    result.data.registerLeadProvider.numberOfWillsDownloaded;
                  this.buttonLoading = false;
                  this.showSuccess = true;
                  this.clearFormData();
                } else {
                  this.onError(result.data.registerLeadProvider.message);
                }
              })
              .catch(error => {
                this.onError(error);
              });
          } else {
            const errors = this.$refs.observer.ctx.errors;
            document
              .getElementById(
                Object.keys(errors).find(field => errors[field].length)
              )
              .scrollIntoView({ block: 'center', behavior: 'smooth' });
          }
        });
      },
      closeDialog() {
        this.dialog = false;
      },
      generateTimeOptions() {
        const options = [];
        for (let h = 0; h < 24; h++) {
          for (let m = 0; m < 60; m += 10) {
            const hour = h.toString().padStart(2, '0');
            const minute = m.toString().padStart(2, '0');
            options.push({
              label: `${hour}:${minute}`,
              value: `${hour}:${minute}`,
            });
          }
        }
        return options;
      },
      scrollToSelectedTime() {
        this.$nextTick(() => {
          const menu = document.querySelector('.time-select-menu .v-list');
          if (!menu) return;
          let selected = menu.querySelector(
            '.v-list-item--active, .v-list-item--selected'
          );
          if (!selected && this.syncTime) {
            // Fallback: find by text content
            const items = menu.querySelectorAll('.v-list-item, .v-list__tile');
            selected = Array.from(items).find(
              el => el.textContent.trim() === this.syncTime
            );
          }
          if (selected) {
            selected.scrollIntoView({ block: 'center' });
          }
        });
      },
    },
    apollo: {
      leadProviderDashboard: {
        query: LEAD_PROVIDER_DASHBOARD_QUERY,
        variables() {
          return {
            id: parseInt(this.$store.getters.userId),
          };
        },
      },
    },
  };
</script>
<style lang="scss">
  input[name='telephone'] {
    display: none !important;
  }
</style>
<style lang="scss" scoped>
  .statistics-card {
    border-radius: 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    height: 100%;

    .statistics-title {
      font-size: 14px;
      font-weight: 500;
      color: #666;
      padding: 16px;
      padding-bottom: 8px;
      text-align: center;
      justify-content: center;
    }

    .statistics-number {
      font-size: 24px;
      font-weight: 600;
      color: var(--v-primary-base);
      padding: 8px 16px 16px;
      text-align: center;
    }
  }
</style>
